import type { LocaleMessages } from "@/i18n/types";
import type { LocaleContent } from "@/types/site";

export const esMessages: LocaleMessages = {
  actions: {
    backToHome: "Volver al inicio",
    copyLink: "Copiar enlace",
    copyLinkSuccess: "Enlace copiado al portapapeles",
    openMenu: "Abrir menú de navegación",
    closeMenu: "Cerrar menú de navegación",
    share: "Compartir",
    signIn: "Iniciar sesión",
    signingIn: "Iniciando sesión…",
    signOut: "Cerrar sesión",
    submit: "Enviar",
    submitting: "Enviando…",
    switchLanguage: "Cambiar idioma",
  },
  auth: {
    loggedInAs: "Sesión iniciada como",
    mockOnly: "Solo para pruebas locales. No lleves estas credenciales a producción.",
    seededAccounts: "Cuentas de ejemplo",
    seedHint: "Redirige a {target} después de iniciar sesión.",
    useCredentials: "Usa estas credenciales en local.",
  },
  common: {
    description: "Descripción",
    email: "Correo electrónico",
    formErrors: "Corrige los campos señalados e inténtalo de nuevo.",
    loading: "Cargando…",
    optional: "Opcional",
    password: "Contraseña",
    required: "Obligatorio",
    skipToContent: "Saltar al contenido principal",
    toggleNav: "Mostrar u ocultar navegación",
  },
  consent: {
    accept: "Aceptar analítica",
    decline: "Rechazar",
    description:
      "Usamos analítica respetuosa con la privacidad para ver qué partes del portafolio se usan. Sin cookies. Sin seguimiento entre sitios.",
    learnMore: "Leer la nota de privacidad",
    title: "Ayuda a mejorar el sitio",
  },
  dashboard: {
    accountNav: "Cuenta",
    adminNav: "Administración",
    overviewNav: "Resumen",
    signedOut: "Has cerrado sesión.",
    welcome: "Bienvenido de nuevo",
  },
  errors: {
    boundaryHint: "Intenta recargar la página. Si sigue pasando, comunícate directamente.",
    boundaryTitle: "Algo salió mal al renderizar esta vista.",
    notFoundIntro: "Esa página no es parte del portafolio público.",
    notFoundTitle: "Página no encontrada.",
    unknownLocale: "Idioma desconocido. Se usará inglés por defecto.",
  },
  share: {
    email: "Compartir por correo",
    linkedin: "Compartir en LinkedIn",
    native: "Compartir…",
    twitter: "Compartir en X",
  },
  sitemap: {
    jumpToSection: "Ir a la sección",
  },
};

export const siteContentEs: LocaleContent = {
  contact: {
    description:
      "El correo es la mejor forma de contactarme. Estoy abierto a oportunidades de software engineering, trabajo de producto y conversaciones con personas construyendo cosas útiles.",
    methods: [
      {
        href: "mailto:i.ramosvillafane@gmail.com",
        label: "Correo",
        showValue: false,
        value: "i.ramosvillafane@gmail.com",
      },
      {
        href: "tel:+18027331254",
        label: "Teléfono",
        showValue: false,
        value: "(802) 733-1254",
      },
      {
        external: true,
        href: "https://github.com/igrv2003",
        label: "GitHub",
        showValue: false,
        value: "github.com/igrv2003",
      },
      {
        external: true,
        href: "https://www.linkedin.com/in/ian-g-ramos/",
        label: "LinkedIn",
        showValue: false,
        value: "linkedin.com/in/ian-g-ramos",
      },
    ],
    resume: {
      download: true,
      href: "/resume/ian-gabriel-ramos-villafane-resume.pdf",
      label: "Descargar resume",
    },
    title: "Contacto",
  },
  contactDetails: {
    availability:
      "Disponible para internados, roles junior/new grad en software engineering y trabajo técnico de producto.",
    email: "i.ramosvillafane@gmail.com",
    responseWindow: "Normalmente respondo dentro de dos días laborables.",
  },
  experience: {
    items: [
      {
        company: "YouTube",
        icon: "/icons/youtube-icon.svg",
        contributions: [
          "Construí un componente UI reutilizable para YouTube Search, permitiendo refinar y filtrar una búsqueda sin salir del feed.",
          "Trabajé desde componentes existentes de la homepage de YouTube en vez de crear una superficie aislada desde cero.",
          "Trabajé con el equipo CatNorvi para conectar datos categóricos de búsqueda con flujos de exploración.",
        ],
        demonstrates:
          "Puedo trabajar dentro de una superficie grande, coordinar con otro equipo y mantener el trabajo frontend reusable en vez de hacerlo one-off.",
        impact:
          "Añadí refinamiento y filtrado de consultas dentro del feed de YouTube Search.",
        location: "San Bruno, CA",
        period: "May 2025 – Aug 2025",
        role: "Software Engineering Intern",
        summary:
          "Trabajé en YouTube Search, construyendo un componente frontend reutilizable para refinamiento de consultas, filtros y browsing exploratorio dentro del feed.",
        team: "YouTube Search",
        technologies: [
          "Ingeniería frontend",
          "Integración de componentes UI",
          "Refinamiento de consultas",
          "Experimentación en búsqueda",
          "Colaboración entre equipos",
        ],
      },
      {
        company: "Google Cloud",
        icon: "/icons/google-cloud-icon.svg",
        contributions: [
          "Integré trabajo alineado con FedRAMP en Google Distributed Cloud Air-Gapped para despliegues en entornos aislados.",
          "Construí soporte de DNS cifrado con TLS para clusters de Kubernetes.",
          "Extendí el Cluster DNS API y el flujo de templating de CoreDNS para soportar comunicación cifrada.",
        ],
        demonstrates:
          "Puedo trabajar en infraestructura donde seguridad, confiabilidad y restricciones reales importan desde el principio.",
        impact:
          "Trabajé en DNS seguro y comunicación cifrada para Google Distributed Cloud Air-Gapped.",
        location: "Sunnyvale, CA",
        period: "May 2024 – Aug 2024",
        role: "Software Engineering Intern",
        summary:
          "Contribuí a infraestructura de Google Cloud para entornos restringidos, con trabajo en seguridad DNS, Kubernetes, CoreDNS, TLS y requisitos alineados con FedRAMP.",
        team: "Google Distributed Cloud Air-Gapped",
        technologies: [
          "Google Cloud",
          "Kubernetes",
          "CoreDNS",
          "TLS",
          "FedRAMP",
          "Infraestructura cloud",
        ],
      },
      {
        company: "YouTube",
        icon: "/icons/youtube-icon.svg",
        contributions: [
          "Construí una interfaz de búsqueda en YouTube Studio con TypeScript y HTML para flujos de trabajo de partners de Primetime.",
          'Añadí un filtro "Video Type" en la sección Content para manejar contenido Subscription Video on Demand.',
          "Llevé el feature por diseño, implementación, code review, coordinación de Bug Bash y lanzamiento interno.",
        ],
        demonstrates:
          "Puedo llevar un feature más allá del happy path: requisitos, implementación, revisión, pruebas y detalles de lanzamiento.",
        impact:
          "Lancé una interfaz de búsqueda especializada en YouTube Studio para contenido de partners de Primetime.",
        location: "Alexandria, VA",
        period: "May 2023 – Aug 2023",
        role: "Software Engineering Intern",
        summary:
          "Construí y lancé una interfaz de búsqueda en YouTube Studio que ayudó a partners de Primetime a filtrar y manejar contenido Subscription Video on Demand.",
        team: "YouTube Studio",
        technologies: [
          "TypeScript",
          "HTML",
          "Desarrollo frontend",
          "Interfaces de búsqueda",
          "Herramientas de gestión de contenido",
          "Procesos de lanzamiento interno",
        ],
      },
    ],
  },
  footerGroups: [
    {
      links: [
        { href: "/experience", label: "Experiencia" },
        { href: "/projects", label: "Proyectos" },
        { href: "/contact", label: "Contacto" },
      ],
      title: "Trabajo",
    },
    {
      links: [
        { href: "/skills", label: "Habilidades" },
        { href: "/legal", label: "Legal" },
        { href: "/", label: "Inicio" },
      ],
      title: "Info",
    },
  ],
  home: {
    closing: {
      action: {
        href: "/contact",
        label: "Hablemos",
      },
      description:
        "Si estás construyendo algo que necesita a alguien que pueda pensar el producto y escribir el código, estoy abierto a hablar.",
      title: "¿Necesitas a alguien que pueda asumir más que un ticket?",
    },
    hero: {
      actions: [
        { href: "/projects", label: "Ver proyectos" },
        { href: "/contact", label: "Hablemos" },
      ],
      description:
        "Soy estudiante de software engineering en Puerto Rico. Construyo apps iOS, productos web y sistemas backend. Mis internados en Google y YouTube importan, pero no son toda la historia. Mi base viene de construir cosas propias.",
      name: "Ian Gabriel Ramos Villafañe",
      portrait: {
        alt: "Ian Gabriel Ramos Villafañe",
        locationLabel: "Mayagüez, Puerto Rico",
        monogram: "IG",
        note: "Estudiante de software engineering construyendo apps, websites y sistemas backend.",
        src: "/brand/home-photo.png",
      },
      title: "Construyo software del que puedo hacerme responsable.",
    },
    proof: {
      items: [
        {
          lines: [
            "Gran parte de lo que he aprendido viene de proyectos donde nadie me entregó un spec.",
            "En Akii y Apolo Inventions he tenido que decidir qué existe, qué se corta y qué vale la pena lanzar.",
          ],
          title: "Construir desde cero",
        },
        {
          lines: [
            "Los side projects enseñan lo que no aparece en mockups: auth, caching, routing, deployment, estados rotos, caminos lentos y mantenimiento.",
            "Ahí aprendí a cuidar código que aguanta después del primer demo.",
          ],
          title: "Ingeniería en las partes incómodas",
        },
        {
          lines: [
            "Google y YouTube me dieron un estándar más alto para reviews, testing, docs y detalles de lanzamiento.",
            "Lo uso como una ventaja que llevo a mi propio trabajo, no como la única razón por la que puedo construir.",
          ],
          title: "Internados como prueba, no identidad",
        },
      ],
    },
    timeline: {
      items: [
        {
          description:
            "Construí la app iOS de fotos, definí la dirección del producto y trabajé auth, perfiles, uploads, feeds y flujos backend.",
          title: "Akii · Producto iOS",
        },
        {
          description:
            "Empecé un negocio de desarrollo web para negocios locales, manejando conversaciones con clientes, branding, builds, deployment y soporte.",
          title: "Apolo Inventions · Trabajo web",
        },
        {
          description:
            "Trabajé en superficies de producción dentro de YouTube Studio, Google Cloud y YouTube Search.",
          title: "Google + YouTube · Internados",
        },
      ],
      title: "Desde dónde construyo",
    },
  },
  legal: {
    sections: [
      {
        body: [
          "Este sitio comparte experiencia profesional, proyectos seleccionados y contexto de portafolio para uso profesional y reclutamiento.",
          "Las descripciones de proyectos se mantienen públicas y seguras para resume. No incluyen detalles confidenciales de implementación.",
        ],
        title: "Uso del portafolio",
      },
    ],
    updatedAt: "2026-05-01",
  },
  navigation: [
    { href: "/", label: "Inicio" },
    { href: "/experience", label: "Experiencia" },
    { href: "/projects", label: "Proyectos" },
    { href: "/skills", label: "Habilidades" },
    { href: "/contact", label: "Contacto" },
  ],
  pages: {
    contact: {
      description:
        "Contacta a Ian Ramos para oportunidades de software engineering, trabajo técnico de producto o conversaciones sobre proyectos.",
      eyebrow: "Contacto",
      title: "Hablemos.",
    },
    experience: {
      description:
        "Trabajo de internado en YouTube Search, Google Cloud y YouTube Studio.",
      eyebrow: "Experiencia",
      title: "Trabajo desde UI de producto hasta infraestructura cloud.",
    },
    home: {
      description:
        "Portafolio de Ian Gabriel Ramos Villafañe, estudiante de software engineering en Puerto Rico construyendo apps iOS, websites, sistemas backend e ideas de producto propias.",
      eyebrow: "Inicio",
      title: "Ian Ramos | Portafolio de Software Engineering",
    },
    projects: {
      description:
        "Trabajo independiente donde asumí el producto, la implementación o el camino desde idea hasta deployment.",
      eyebrow: "Proyectos",
      title: "Proyectos que he construido fuera de internados.",
    },
    skills: {
      description:
        "Lenguajes, frameworks, plataformas y herramientas que uso en trabajo mobile, web, backend e infraestructura.",
      eyebrow: "Habilidades",
      title: "El stack que más uso.",
    },
  },
  projects: {
    items: [
      {
        links: [
          {
            external: true,
            href: "https://akiiapp.com",
            label: "Link",
          },
        ],
        name: "Akii",
        role: "Definí la dirección del producto, diseñé la experiencia y construí la app iOS.",
        summary:
          "Empecé Akii porque la mayoría de las apps de fotos se sienten hechas para seguir scrolleando. Yo quería lo contrario: un lugar más lento para fotos reales tomadas por personas. Construí la app iOS con autenticación, perfiles, uploads, navegación del feed, manejo de imágenes, interacciones sociales y data flows con Supabase.",
        shortDescription:
          "App iOS para fotos reales tomadas por personas.",
        status: "Concepto y codebase público con trabajo de producto en curso.",
        technologies: [
          "Swift",
          "SwiftUI",
          "Supabase",
          "Authentication",
          "Image caching",
          "Feed architecture",
        ],
      },
      {
        name: "Personal Finance App",
        role: "Trabajé en entrada de gastos, análisis de spending y recordatorios mediante notificaciones.",
        summary:
          "Este proyecto se trató de hacer que los gastos personales fueran más fáciles de registrar. Trabajé en las partes que permiten entrar gastos, revisar patrones de spending y recibir notificaciones cuando algo necesita atención.",
        shortDescription:
          "Aplicación web para registrar gastos y revisar patrones de spending.",
        status: "Los detalles públicos del proyecto todavía se están preparando.",
        technologies: [
          "Web development",
          "Frontend engineering",
          "Data visualization",
          "Notifications",
          "Financial tracking",
        ],
      },
      {
        links: [
          {
            external: true,
            href: "https://apoloinv.com",
            label: "Link",
          },
        ],
        name: "Apolo Inventions",
        role: "Manejo el trabajo desde conversaciones con clientes hasta build, deployment y soporte.",
        summary:
          "Apolo Inventions es mi negocio de desarrollo web para negocios locales que necesitan una presencia online mejor que un perfil de redes sociales. El trabajo es simple a propósito: entender qué necesita el negocio, darle forma a la marca, construir el site, desplegarlo y mantenerlo corriendo.",
        shortDescription:
          "Negocio de desarrollo web para websites de negocios locales.",
        status: "Trabajo activo con clientes y desarrollo de plataforma interna.",
        technologies: [
          "Web development",
          "Brand systems",
          "Client communication",
          "Deployment workflows",
          "Cloudflare hosting",
        ],
      },
    ],
  },
  skillsPage: {
    description:
      "Una lista práctica de las herramientas que más uso en trabajo mobile, web, backend e infraestructura.",
    groups: [
      {
        items: ["Swift", "SwiftUI", "Xcode", "App Store"],
        title: "iOS",
      },
      {
        items: [
          "TypeScript",
          "JavaScript",
          "React",
          "Vite",
          "Tailwind CSS",
          "HTML",
          "CSS",
          "Markdown",
          "Node.js",
        ],
        title: "Web",
      },
      {
        items: [
          "Supabase",
          "PostgreSQL",
          "REST APIs",
          "Cloudflare",
          "Kubernetes",
          "CoreDNS",
        ],
        title: "Backend e Infraestructura",
      },
      {
        items: [
          "Git",
          "GitHub",
          "Figma",
          "Notion",
          "Codex",
          "Claude",
          "Google Gemini",
        ],
        title: "Workflow y AI",
      },
    ],
    title: "Herramientas con las que construyo.",
  },
  site: {
    description:
      "Portafolio de software engineering de Ian Gabriel Ramos Villafañe, con proyectos independientes, habilidades técnicas, experiencia de internado e información de contacto.",
    name: "Ian Ramos",
    tagline:
      "Estudiante de software engineering construyendo apps iOS, websites, sistemas backend e ideas de producto.",
  },
};
